import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useAuth } from 'hooks/UseAuth'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useBrandKitState } from 'flows/common/hooks/useBrandKitState'

import { FlowRenderer } from 'flows/common/components/FlowRenderer'
import { onboardingSteps } from 'flows/onboarding/steps'
import { brandKitSteps } from 'flows/brandKitCreation/steps'
import { firstCreationSteps } from 'flows/firstCreation/firstCreationSteps'

import { BrandKit } from 'types'
import { useOnboardingAndBrandKitFlow } from 'flows/onboarding/useOnboardingFlow'
import { useTrackRegistrationSteps } from 'hooks/useTrackRegistrationSteps'

export const OnboardingAndBrandKit: React.FC = () => {
  /* 1. flow ---------------------------------------------------------- */
  const { current, send, isLoading: booting, isDone } = useOnboardingAndBrandKitFlow()

  /* 2. queries ------------------------------------------------------- */
  const auth = useAuth()
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const { data: kits } = useBrandKits({ enabled: !!auth })
  useTrackRegistrationSteps(current)

  const initRef = useRef(false)

  /* 3. brand-kit fetch / local palette+font state ------------------- */
  const [kitId, setKitId] = useState<number>()
  useEffect(() => {
    if (!kitId && kits?.length) setKitId(kits[0].id)
  }, [kits, kitId])

  const { data: kitResp, isLoading: kitLoading } = useBrandKit(kitId, { enabled: !!kitId })

  const { colorPaletteState, setColorPaletteState, fontPairingState, setFontPairingState, initFromBrandKit } =
    useBrandKitState()

  useEffect(() => {
    if (kitResp?.brand_kit && !initRef.current) {
      initFromBrandKit(kitResp.brand_kit)
      initRef.current = true
    }
  }, [kitResp, initFromBrandKit])

  /* helper to keep list cache in sync -------------------------------- */
  const setBrandKit = (bk: BrandKit) => {
    queryClient.setQueryData(['workspaces', workspaceId, 'brandKits', 'list'], (old: BrandKit[] = []) => {
      const i = old.findIndex((k) => k.id === bk.id)
      return i === -1 ? [bk, ...old] : [...old.slice(0, i), bk, ...old.slice(i + 1)]
    })
    setKitId(bk.id)
  }

  /* 4. ctx object passed to steps ----------------------------------- */
  const ctx = useMemo(
    () => ({
      brandKit: kitResp?.brand_kit,
      setBrandKit,
      auth,
      colorPaletteState,
      setColorPaletteState,
      fontPairingState,
      setFontPairingState,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kitResp?.brand_kit, setBrandKit, auth, colorPaletteState, fontPairingState],
  )

  /* 5. flow descriptor list ----------------------------------------- */
  const allSteps = useMemo(() => [...onboardingSteps, ...brandKitSteps, ...firstCreationSteps], [])

  const isBusy = booting || kitLoading
  return <FlowRenderer current={current} send={send} steps={allSteps} ctx={ctx} isLoading={isBusy} isDone={isDone} />
}
